import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaExclamation, FaCheck } from 'react-icons/fa';
import { RiMoreFill } from 'react-icons/ri';
import { MdExpandMore, MdExpandLess } from 'react-icons/md';
import Player from '@vimeo/player';
import axios from 'config/axios';
import Popup from 'components/Popup';
import Loader from 'components/Loader';

import './StudentLectures.css';

interface LectureData {
    id: number;
    lectureNumber: number;
    name: string;
    videoUrl: string;
    duration: number;
    progress: number;
    completed: boolean;
    critical: boolean;
    recommended: boolean;
    category: string | null;
    categoryProficiency: number | null;
}

interface LectureGroupData {
    groupNumber: number;
    name: string;
    duration: number;
    proficiency: number | null;
    lectures: LectureData[];
}

interface CourseData {
    id: number;
    name: string;
    criticalCount: number;
    recommendedCount: number;
    lectureGroups: LectureGroupData[];
}

interface RecommendationsData {
    firstName: string;
    lastName: string;
    courses: CourseData[];
}

interface Topic {
    category: string;
    proficiency: number | null;
    critical: boolean;
    recommended: boolean;
    lectures: LectureData[];
    completedCount: number;
    totalCount: number;
}

function getProficiencyLevel(proficiency: number | null): string {
    if (proficiency === null) return 'none';
    if (proficiency <= 40) return 'critical';
    if (proficiency <= 80) return 'recommended';
    return 'satisfactory';
}

function formatDuration(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    if (minutes >= 60) {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${minutes}m`;
}

function buildTopics(course: CourseData): Topic[] {
    const topicMap: Record<string, Topic> = {};

    for (const group of course.lectureGroups) {
        for (const lecture of group.lectures) {
            if (!lecture.category) continue;
            const key = lecture.category;
            if (!topicMap[key]) {
                topicMap[key] = {
                    category: key,
                    proficiency: lecture.categoryProficiency,
                    critical: false,
                    recommended: false,
                    lectures: [],
                    completedCount: 0,
                    totalCount: 0
                };
            }
            topicMap[key].lectures.push(lecture);
            topicMap[key].totalCount += 1;
            if (lecture.completed) topicMap[key].completedCount += 1;
            if (lecture.critical) topicMap[key].critical = true;
            if (lecture.recommended && !topicMap[key].critical) topicMap[key].recommended = true;
        }
    }

    return Object.values(topicMap).sort((a, b) => {
        const ap = a.proficiency ?? 999;
        const bp = b.proficiency ?? 999;
        return ap - bp;
    });
}

interface StudentLecturesProps {
    studentId: string;
}

function StudentLectures({ studentId }: StudentLecturesProps) {
    const [data, setData] = useState<RecommendationsData | null>(null);
    const [courseIndex, setCourseIndex] = useState(0);
    const [expandedTopics, setExpandedTopics] = useState<Set<string>>(new Set());
    const [selectedLecture, setSelectedLecture] = useState<LectureData | null>(null);
    const playerRef = useRef<any>(null);

    useEffect(() => {
        axios.get(`tutors/students/${studentId}/lectureRecommendations/`)
            .then(res => setData(res.data))
            .catch(error => console.log(error));
    }, [studentId]);

    useEffect(() => {
        setExpandedTopics(new Set());
    }, [courseIndex]);

    useEffect(() => {
        if (selectedLecture === null) {
            if (playerRef.current) {
                playerRef.current.destroy();
                playerRef.current = null;
            }
            return;
        }

        if (playerRef.current) {
            playerRef.current.loadVideo(selectedLecture.videoUrl);
        } else {
            playerRef.current = new Player('studentLecturesVimeoPlayer', {
                url: selectedLecture.videoUrl as any
            });
        }
    }, [selectedLecture]);

    const currentCourse = data?.courses[courseIndex] ?? null;

    const topics = useMemo(() => {
        if (!currentCourse) return [];
        return buildTopics(currentCourse);
    }, [currentCourse]);

    const toggleTopic = (category: string) => {
        const next = new Set(expandedTopics);
        if (next.has(category)) {
            next.delete(category);
        } else {
            next.add(category);
        }
        setExpandedTopics(next);
    };

    if (!data) return <Loader />;

    const hasWeaknessData = topics.some(t => t.proficiency !== null);

    return (
        <div>
            {selectedLecture && (
                <Popup
                    title={selectedLecture.name}
                    secondaryButtonText="Close"
                    secondaryButtonOnClick={() => setSelectedLecture(null)}
                >
                    <div className="studentLectures__videoContainer">
                        <div id="studentLecturesVimeoPlayer"></div>
                    </div>
                </Popup>
            )}

            {data.courses.length > 1 && (
                <div className="studentLectures__courseSelector">
                    {data.courses.map((course, i) => (
                        <div
                            key={course.id}
                            className={i === courseIndex
                                ? 'studentLectures__courseCard--selected'
                                : 'studentLectures__courseCard'
                            }
                            onClick={() => setCourseIndex(i)}
                        >
                            {course.name}
                        </div>
                    ))}
                </div>
            )}

            {!hasWeaknessData && topics.length > 0 && (
                <div className="studentLectures__empty">
                    No weakness data available — student needs to complete a test first.
                </div>
            )}

            {currentCourse && hasWeaknessData && (currentCourse.criticalCount > 0 || currentCourse.recommendedCount > 0) && (
                <div className="studentLectures__counts">
                    {currentCourse.criticalCount > 0 && (
                        <div className="studentLectures__countItem">
                            <span className="studentLectures__countIcon studentLectures__countIcon--critical">
                                <FaExclamation />
                            </span>
                            {currentCourse.criticalCount} critical
                        </div>
                    )}
                    {currentCourse.recommendedCount > 0 && (
                        <div className="studentLectures__countItem">
                            <span className="studentLectures__countIcon studentLectures__countIcon--recommended">
                                <RiMoreFill />
                            </span>
                            {currentCourse.recommendedCount} advised to review
                        </div>
                    )}
                </div>
            )}

            {topics.length > 0 && (
                <div className="studentLectures__topicList">
                    {topics.map(topic => {
                        const isExpanded = expandedTopics.has(topic.category);
                        const level = getProficiencyLevel(topic.proficiency);
                        const progressRatio = topic.totalCount > 0
                            ? topic.completedCount / topic.totalCount
                            : 0;

                        return (
                            <React.Fragment key={topic.category}>
                                <div
                                    className={isExpanded
                                        ? 'studentLectures__topic--expanded'
                                        : 'studentLectures__topic'
                                    }
                                    onClick={() => toggleTopic(topic.category)}
                                >
                                    <span className={`studentLectures__topicBadge studentLectures__topicBadge--${level}`}>
                                        {level === 'critical' && <FaExclamation />}
                                        {level === 'recommended' && <RiMoreFill />}
                                        {level === 'satisfactory' && <FaCheck />}
                                    </span>

                                    <span className="studentLectures__topicName">
                                        {topic.category}
                                    </span>

                                    {topic.proficiency !== null && (
                                        <span className={`studentLectures__topicProficiency studentLectures__topicProficiency--${level}`}>
                                            {Math.round(topic.proficiency)}%
                                        </span>
                                    )}

                                    <div className="studentLectures__topicProgress">
                                        <div className="studentLectures__topicProgressBar">
                                            <div
                                                className="studentLectures__topicProgressBarFill"
                                                style={{ width: `${progressRatio * 100}%` }}
                                            />
                                        </div>
                                        <span className="studentLectures__topicProgressText">
                                            {topic.completedCount}/{topic.totalCount}
                                        </span>
                                    </div>

                                    <span className="studentLectures__topicExpandIcon">
                                        {isExpanded ? <MdExpandLess /> : <MdExpandMore />}
                                    </span>
                                </div>

                                {isExpanded && (
                                    <div className="studentLectures__lectures">
                                        {topic.lectures.map(lecture => (
                                            <div
                                                key={lecture.id}
                                                className="studentLectures__lecture"
                                                onClick={() => setSelectedLecture(lecture)}
                                            >
                                                <span className="studentLectures__lectureStatus">
                                                    {lecture.completed && (
                                                        <FaCheck className="studentLectures__lectureCompleted" />
                                                    )}
                                                </span>
                                                <span className="studentLectures__lectureName">
                                                    {lecture.name}
                                                </span>
                                                <span className="studentLectures__lectureDuration">
                                                    {formatDuration(lecture.duration)}
                                                </span>
                                                <div className="studentLectures__lectureProgress">
                                                    <div
                                                        className="studentLectures__lectureProgressBar"
                                                        style={{ width: `${lecture.progress * 100}%` }}
                                                    />
                                                </div>
                                                <span className="studentLectures__lecturePercent">
                                                    {Math.round(lecture.progress * 100)}%
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            )}

            {topics.length === 0 && data.courses.length > 0 && (
                <div className="studentLectures__empty">
                    No lecture recommendations available.
                </div>
            )}

            {data.courses.length === 0 && (
                <div className="studentLectures__empty">
                    No lectures available.
                </div>
            )}
        </div>
    );
}

export default StudentLectures;
